import { SelectionMenuViewMode } from '@components/ButtonBaseWithSelectionMenu/types/buttonBaseWithSelectionMenu.types';
import useWidth from '@components/Hooks/useWidth';
import ProductLock from '@components/Product/ProductLock';
import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { useProductLock } from '../../../../products/products-details-v2/hooks/useProductLock';
import { useOrderCalculateContext } from '../../../contexts/OrderCalculate.context';
import OrderEditPageSummary from '../../../edit/summary/components/OrderEditPageSummary';
import { Order } from '../../../order.types';
import OrderProducts from '../../../products/OrderProducts';
import useAddProductToggle from '../hooks/useAddProductToggle';
import useOrderObjectTabConditionReset from '../hooks/useOrderObjectTabConditionReset';
import useOrderObjectTabSectionSync from '../hooks/useOrderObjectTabSectionSync';
import { OrderTabKey } from '../orderObjectPage.types';
import AddProductToggleButton from './AddProductToggleButton/AddProductToggleButton';
import EditSuggestedOrderButton from './EditSuggestedOrderButton';
import OrderObjectActivitiesTab from './OrderObjectActivitiesTab/OrderObjectActivitiesTab';
import OrderObjectConditionsTab from './OrderObjectConditionsTab/OrderObjectConditionsTab';
import OrderObjectOverviewTab from './OrderObjectOverviewTab';
import OrderObjectTargetsTab from './OrderObjectTargetsTab/OrderObjectTargetsTab';
import OrderProductsViewButton from './OrderProductsViewButton/OrderProductsViewButton';

interface OrderObjectTabContentProps {
  activeTab: OrderTabKey;
  order: Order;
  canEditOrder: boolean;
  isReadOnlyView: boolean;
  isOfflineMode?: boolean;
}

const OrderObjectTabContent = ({
  activeTab,
  order,
  canEditOrder,
  isReadOnlyView,
  isOfflineMode,
}: OrderObjectTabContentProps) => {
  useOrderObjectTabSectionSync(activeTab);
  useOrderObjectTabConditionReset(activeTab);

  const { summaryTotals } = useOrderCalculateContext();
  const { mobileWidth } = useWidth();
  const hasProducts = (summaryTotals?.calculate?.total?.quantity ?? 0) > 0;

  const { isLocked, isLockEnabled, onClickLock } = useProductLock();

  const isSuggestedPendingAcceptance =
    order.status === 'SUGGESTED' && !order.archived && !canEditOrder;
  const canEditProducts = canEditOrder && !isReadOnlyView;
  const hasLightSummaryBar = isSuggestedPendingAcceptance || isReadOnlyView;

  const {
    isOpen: isAddProductOpen,
    mode: addProductMode,
    isModeSelectable: isAddProductModeSelectable,
    isPreferenceReady: isAddProductPreferenceReady,
    toggleOpen: toggleAddProduct,
    selectMode: selectAddProductMode,
  } = useAddProductToggle({ orderId: order.id, hasProducts, isOfflineMode });

  const tabContent: Record<OrderTabKey, ReactNode> = {
    overview: <OrderObjectOverviewTab order={order} />,
    activities: <OrderObjectActivitiesTab orderId={order.id} />,
    targets: <OrderObjectTargetsTab orderId={order.id} />,
    conditions: (
      <OrderObjectConditionsTab
        order={order}
        isReadOnlyView={isReadOnlyView}
        isOfflineMode={isOfflineMode}
      />
    ),
    products: (
      <>
        <ObjectScreen.Controls>
          {canEditProducts && isAddProductPreferenceReady && (
            <AddProductToggleButton
              isOpen={isAddProductOpen}
              mode={addProductMode}
              isModeSelectable={isAddProductModeSelectable}
              isCompact={mobileWidth}
              onToggleOpen={toggleAddProduct}
              onSelectMode={selectAddProductMode}
            />
          )}

          <OrderProductsViewButton />
          {canEditProducts && isLockEnabled && (
            <ProductLock isLocked={isLocked} onClick={onClickLock} />
          )}
        </ObjectScreen.Controls>
        <OrderProducts
          isOfflineMode={isOfflineMode}
          orderId={order.id}
          orderStatus={order.status}
          viewMode={isReadOnlyView ? SelectionMenuViewMode.VIEW : SelectionMenuViewMode.EDIT}
          canEditOrder={canEditProducts}
          isAddProductOpen={isAddProductOpen}
          addProductMode={addProductMode}
          orderBrandHasIntegrations={!!order.hasBrandIntegrations}
          onToggleAddProduct={toggleAddProduct}
        />
      </>
    ),
  };

  return (
    <Box sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
      {isSuggestedPendingAcceptance && (
        <ObjectScreen.Controls>
          <EditSuggestedOrderButton order={order} />
        </ObjectScreen.Controls>
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {tabContent[activeTab]}
      </Box>

      {activeTab === 'products' && (hasProducts || isAddProductOpen) && (
        <Box sx={{ position: 'sticky', bottom: 0, zIndex: 10 }}>
          <OrderEditPageSummary isSuggested={hasLightSummaryBar} isReadOnlyView={isReadOnlyView} />
        </Box>
      )}
    </Box>
  );
};

export default OrderObjectTabContent;
